import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import styles from '../../styles/components/sections/FleetSection.module.css';
import { useDarkMode } from '../../utils/hooks/useDarkMode';
import { OptimizedImage } from '../common';

const FleetSection = ({ fleetData, className = '' }) => {
    const { isDarkMode } = useDarkMode();
    const router = useRouter();
    const [activeTab, setActiveTab] = useState(0);
    const [activeAccordion, setActiveAccordion] = useState(null);

    const toggleAccordion = (index) => {
        setActiveAccordion(activeAccordion === index ? null : index);
    };

    if (!fleetData || !fleetData.fleets || fleetData.fleets.length === 0) {
        return null;
    }

    // Get all fleets from the data
    const allFleets = fleetData.fleets;
    
    // Check if any fleet has vehicles
    const hasVehicles = allFleets.some(fleet => fleet.Vehicle && fleet.Vehicle.length > 0);
    
    if (!hasVehicles) {
        return null;
    }

    // console.log("fleetData",fleetData);

    const renderVehicleCard = (vehicleData, index) => (
        <div key={index} className={`col-lg-4 col-md-6 mb-4`}>
            <div className={`${styles.vehicleCard} ${isDarkMode ? styles['dark-card'] : ''}`}>
                <div className={styles.vehicleImageContainer}>
                    {vehicleData?.image?.image?.url && (
                        <OptimizedImage
                            src={vehicleData.image.image.url}
                            alt={vehicleData.image.image.alternativeText || vehicleData.headline}
                            className={styles.vehicleImage}
                            width={400}
                            height={250}
                            loading="lazy"
                            priority={false}
                            sizes="(max-width: 768px) 100vw, (max-width: 992px) 50vw, 33vw"
                            quality={65}
                            style={{objectFit: 'contain', backgroundColor: 'var(--fleet-card-bg)'}}
                        />
                    )}
                </div>
                
                <div className={styles.vehicleContent}>
                    {vehicleData?.Button?.url ? (
                        <Link href={vehicleData.Button.url} className='text-decoration-none'>
                            <p 
                                className={`${styles.vehicleTitle} ${isDarkMode ? styles['dark-title'] : ''}`}
                                style={{ cursor: 'pointer' }}
                            >
                                {vehicleData?.headline}
                            </p>
                        </Link>
                    ) : (
                        <p 
                            className={`${styles.vehicleTitle} ${isDarkMode ? styles['dark-title'] : ''}`}
                        >
                            {vehicleData?.headline}
                        </p>
                    )}
                    <p className={`${styles.vehicleDescription} ${isDarkMode ? styles['dark-description'] : ''}`}>
                        {vehicleData?.title}
                    </p>
                    
                    {vehicleData?.Button?.url && (
                        <Link
                            href={vehicleData.Button.url}
                            className={`${styles.viewDetailsBtn} ${isDarkMode ? styles['dark-btn'] : ''} text-decoration-none`}>
                            {vehicleData.Button.name || 'View Details'}
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );

    const getCurrentVehicles = () => {
        // Get vehicles from the currently active fleet
        if (allFleets[activeTab] && allFleets[activeTab].Vehicle) {
            return allFleets[activeTab].Vehicle;
        }
        return [];
    };

    return (
        <section className={`py-12 ${styles.fleetSection} ${isDarkMode ? styles['dark-section'] : ''}`}>
            <div className="tw-container">
                {/* Header */}
                <div className={styles.fleetHeader}>
                    <h2 className={`${styles.fleetTitle} ${isDarkMode ? styles['dark-heading'] : ''}`}>
                        {fleetData?.title}
                    </h2>
                    <p className={`${styles.fleetDescription} ${isDarkMode ? styles['dark-description'] : ''}`}>
                       {fleetData?.description}
                    </p>
                </div>

                {/* Tab Navigation - Always show tabs */}
                <div className={styles.tabNavigation}>
                    {allFleets.map((fleet, index) => (
                        <button 
                            key={index}
                            className={`${styles.tabButton} ${activeTab === index ? styles.active : ''} ${isDarkMode ? styles['dark-tab'] : ''}`}
                            onClick={() => setActiveTab(index)}
                        >
                            {fleet.name || `Fleet ${index + 1}`}
                        </button>
                    ))}
                </div>

                {/* Vehicle Grid */}
                <div className={styles.vehicleGrid}>
                    <div className="row justify-content-center">
                        {getCurrentVehicles().map((vehicleData, index) => 
                            renderVehicleCard(vehicleData, index)
                        )}
                    </div>
                </div>

                {/* Show message if no vehicles in fleet */}
                {getCurrentVehicles().length === 0 && (
                    <div className={styles.noVehicles}>
                        <p>No vehicles available in this fleet.</p>
                    </div>
                )}
            </div>
        </section>
    );
};

export default FleetSection;

